/**
 * SubscriptionContext
 *
 * Runs useSubscription() once at the app level and shares the result
 * via context. Prevents 16+ components from each making independent
 * subscription checks on mount, which was causing TooManyRequestsException.
 */

import { createContext, useContext, ReactNode } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import type { SubscriptionSource, CompanySubscription } from '../types/subscription';

interface SubscriptionContextValue {
  subscriptionTier: 'free' | 'pro';
  subscriptionDetails: { tier: 'free' | 'pro'; status?: string; trialEnd?: string } | null;
  subscriptionSource: SubscriptionSource;
  companyDomain: string | null;
  companySubscription: CompanySubscription | null;
  isPro: boolean;
  loading: boolean;
  error: string | null;
  refreshSubscription: () => void;
  notifySubscriptionChanged: () => void;
  getUpgradeMessage: () => string;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const subscription = useSubscription();

  // Don't render children until subscription check has started
  // This prevents child components from calling AppSync before auth is ready
  return (
    <SubscriptionContext.Provider value={subscription}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Use subscription data from the shared context.
 * Must be used within SubscriptionProvider (inside authenticated routes).
 */
export function useSubscriptionContext(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscriptionContext must be used within SubscriptionProvider');
  }
  return ctx;
}
